// Pure calculation logic for the BEP page (no side effects, no API calls).

// Parse a form value: returns a non-negative finite number, or null when empty/invalid.
export const parseInput = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
};

// Remove tiny floating-point noise before rounding up (e.g. 600.0000000001 -> 600).
const ceilSafe = (x) => Math.ceil(Number(x.toFixed(6)));
const isZero = (x) => Math.abs(x) < 0.005;

export function computeBep({ fixedCost, price, variable, target, capital }) {
  const F = parseInput(fixedCost);
  const P = parseInput(price);
  const V = parseInput(variable);
  const T = parseInput(target);
  const C = parseInput(capital);

  const baseReady = F !== null && P !== null && V !== null;

  // --- Section A: BEP -------------------------------------------------------
  const contrib = P !== null && V !== null ? P - V : null;
  const bepValid = baseReady && contrib > 0;
  const bepUnit = bepValid ? (F === 0 ? 0 : ceilSafe(F / contrib)) : null;
  const bepOmzet = bepValid ? bepUnit * P : null;

  // --- Section B: Target & operating profit ---------------------------------
  const targetValid = T !== null && Number.isInteger(T);
  let targetResult = null;
  if (baseReady && targetValid) {
    const omzet = T * P;
    const totalVariable = T * V;
    const totalCost = F + totalVariable;
    let profit = omzet - totalCost;
    if (isZero(profit)) profit = 0;
    const status = profit < 0 ? "rugi" : profit === 0 ? "impas" : "laba";
    targetResult = { omzet, totalVariable, totalCost, profit, status };
  }

  // --- Section C: Payback ---------------------------------------------------
  // state: need_capital | no_investment | need_target | loss | zero_profit | ok
  let payback;
  if (C === null) payback = { state: "need_capital" };
  else if (C === 0) payback = { state: "no_investment" };
  else if (!targetResult) payback = { state: "need_target" };
  else if (targetResult.profit < 0) payback = { state: "loss" };
  else if (targetResult.profit === 0) payback = { state: "zero_profit" };
  else {
    const exact = C / targetResult.profit;
    payback = { state: "ok", exact, months: ceilSafe(exact) };
  }

  return {
    inputs: { F, P, V, T, C },
    baseReady, contrib, bepValid, bepUnit, bepOmzet,
    targetValid, targetResult, payback,
  };
}

// Chart rows: revenue vs total cost, always including the BEP point and the target point.
export function buildChartData({ F, P, V, T }, bepUnit) {
  if (F === null || P === null || V === null) return [];
  const maxUnit = Math.max(10, (bepUnit || 0) * 2, T ? Math.ceil(T * 1.2) : 0);
  const step = Math.max(1, Math.round(maxUnit / 10));
  const points = new Set();
  for (let q = 0; q <= maxUnit; q += step) points.add(q);
  points.add(maxUnit);
  if (bepUnit !== null && bepUnit !== undefined) points.add(bepUnit);
  if (T !== null && T !== undefined && Number.isInteger(T)) points.add(T);
  return [...points].sort((a, b) => a - b).map((q) => ({
    unit: q,
    pendapatan: q * P,
    biaya: F + q * V,
  }));
}
